/* eslint no-param-reassign: "off" */
import React, {
  forwardRef,
  useRef,
  useLayoutEffect,
  useState,
  useContext,
  useMemo,
} from "react";
import {BreakpointContext, LARGE} from "@core-ui/breakpoint-provider";
import Slider from "react-slick";
import {ChevronIcon, ChevronSquareIcon} from "@core-ui/icons";
import {css, styled, useTheme} from "@core-ui/react-stitch";
import CarouselContainer from "./components/CarouselContainer";
import AutoplayButton from "./components/AutoplayButton";
import {CarouselProps, ColorTheme, SliderElement} from "./types";

export const Carousel = forwardRef<SliderElement, CarouselProps>(
  function Carousel(props, ref): JSX.Element {
    const {
      arrows = true,
      arrowPosition = "25px",
      arrowVerticalPosition = "50%",
      autoplaySpeed = 3000,
      autoplay,
      className,
      customPlayPauseButton,
      children,
      fade = false,
      infinite = false,
      iconColorTheme = ColorTheme.dark,
      mobileSmoothScroll = false,
      slidesToScroll = 1,
      slidesToShow = 1,
      speed = 500,
      displayPlayPauseButton,
      playAltText,
      pauseAltText,
      nextArrowAlt = "Next",
      prevArrowAlt = "Previous",
      useChevronSquare = false,
      prevArrow,
      nextArrow,
      ...other
    } = props;

    const theme = useTheme();
    const slickRef = useRef<SliderElement>(null);

    const [currentSlideIndex, setCurrentSlideIndex] = useState(
      props.initialSlide || 0
    );

    const sliderChildren: React.ReactNodeArray = Array.isArray(props.children)
      ? props.children
      : [];

    const sliderLength = sliderChildren.length;
    const lastIndex = sliderLength - 1;
    const distance = slidesToShow - 1;

    const isDisabledPrev =
      !infinite && (sliderLength < 2 || currentSlideIndex === 0);

    const isDisabledNext =
      !infinite && currentSlideIndex >= lastIndex - distance;
    const useChevronSquareIcon = theme.brand === "on" && useChevronSquare;

    useLayoutEffect(() => {
      if (ref) {
        if (typeof ref === "function") {
          ref(slickRef.current);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (ref as any).current = slickRef.current;
        }
      }
    }, [ref]);

    const autoplayButton = useMemo(
      () => (
        <AutoplayButton
          colorTheme={iconColorTheme}
          customPlayPauseButton={customPlayPauseButton}
          pauseAltText={pauseAltText}
          playAltText={playAltText}
          slickRef={slickRef}
        />
      ),
      [
        iconColorTheme,
        pauseAltText,
        playAltText,
        slickRef,
        customPlayPauseButton,
      ]
    );

    const arrowIconElement = (
      className: string,
      ariaLabel: string,
      isDisabled: boolean
    ): JSX.Element => {
      const WrapperButton = styled.button`
        position: relative;
        height: 44px;
        &:hover {
          ${isDisabled ? "cursor: initial" : ""}
        }
      `;

      return (
        <WrapperButton
          aria-label={ariaLabel}
          className={className}
          disabled={isDisabled}
          type="button"
        >
          {useChevronSquareIcon ? (
            <ChevronSquareIcon colorTheme={iconColorTheme} />
          ) : (
            <ChevronIcon colorTheme={iconColorTheme} />
          )}
        </WrapperButton>
      );
    };

    const renderedPrevArrow = props.prevArrow
      ? React.cloneElement(props.prevArrow, {isDisabled: isDisabledPrev})
      : arrowIconElement("slick-prev", prevArrowAlt, isDisabledPrev);

    const renderedNextArrow = props.nextArrow
      ? React.cloneElement(props.nextArrow, {isDisabled: isDisabledNext})
      : arrowIconElement("slick-next", nextArrowAlt, isDisabledNext);

    const {greaterOrEqualTo} = useContext(BreakpointContext);
    const isLarge = greaterOrEqualTo(LARGE);

    const handleOnSwipe = () => {
      if (slickRef.current) {
        (slickRef.current as SliderElement).innerSlider.clickable = true;
      }
    };

    /* istanbul ignore next */
    const smoothScrollOnMobile = !isLarge && mobileSmoothScroll;
    /* istanbul ignore next */
    const smoothScrollOptions = smoothScrollOnMobile && {
      centerMode: false,
      cssEase: "linear",
      infinite: false,
      slidesToScroll: 0.1,
      swipeToSlide: true,
      speed: 3000,
      fade: false,
      css: () => css`
        .slick-list {
          overflow-x: auto;
          overflow-y: hidden;
        }
      `,
    };

    return (
      <CarouselContainer
        arrowPosition={arrowPosition}
        arrowVerticalPosition={arrowVerticalPosition}
        className={className}
        fade={fade}
      >
        {autoplay && displayPlayPauseButton !== false && autoplayButton}
        <Slider
          ref={slickRef}
          afterChange={(index) => setCurrentSlideIndex(index)}
          arrows={arrows}
          autoplay={autoplay}
          autoplaySpeed={autoplaySpeed}
          fade={fade}
          infinite={infinite}
          nextArrow={renderedNextArrow}
          onSwipe={handleOnSwipe}
          prevArrow={renderedPrevArrow}
          slidesToScroll={slidesToScroll}
          slidesToShow={slidesToShow}
          speed={speed}
          {...other}
          {...smoothScrollOptions}
        >
          {children}
        </Slider>
      </CarouselContainer>
    );
  }
);
